<div class="w-full bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
  <!-- Header -->
  <div
    class="flex items-center gap-3 px-4 py-3 border-b border-gray-100"
    [ngClass]="{
      'bg-green-50': preview().isReadyForGrading,
      'bg-amber-50': !preview().isReadyForGrading
    }"
  >
    <div
      class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
      [ngClass]="{
        'bg-green-100 text-green-600': preview().isReadyForGrading,
        'bg-amber-100 text-amber-600': !preview().isReadyForGrading
      }"
    >
      <lucide-icon
        [img]="preview().isReadyForGrading ? icons.Check : icons.AlertTriangle"
        class="w-5 h-5"
      ></lucide-icon>
    </div>
    <div class="flex-1 min-w-0">
      <div class="text-sm font-semibold text-gray-900">{{ getTitle() }}</div>
      <div
        class="text-xs"
        [ngClass]="{
          'text-green-600': preview().isReadyForGrading,
          'text-amber-600': !preview().isReadyForGrading
        }"
      >
        {{ getSubtitle() }}
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="p-4">
    <!-- Materials Section -->
    <div class="space-y-2">
      <div class="flex items-center gap-1.5 text-gray-500 font-medium text-xs">
        <lucide-icon [img]="icons.FileText" class="w-4 h-4"></lucide-icon>
        <span>Materials</span>
      </div>
      <div class="space-y-1">
        @for (item of materialsList(); track item.label) {
          <div
            class="flex items-center gap-1.5 text-xs"
            [ngClass]="{
              'text-green-700': item.present,
              'text-amber-600': !item.present && item.required,
              'text-gray-500': !item.present && !item.required
            }"
          >
            <lucide-icon
              [img]="item.present ? icons.Check : icons.X"
              class="w-3.5 h-3.5"
              [ngClass]="{
                'text-green-500': item.present,
                'text-amber-500': !item.present && item.required,
                'text-gray-400': !item.present && !item.required
              }"
            ></lucide-icon>
            <span>{{ item.label }}</span>
            @if (item.count !== undefined && item.count > 0) {
              <span class="text-green-600">({{ item.count }})</span>
            }
            @if (!item.required) {
              <span class="text-gray-400 text-[10px] ml-auto">optional</span>
            }
          </div>
        }
      </div>
    </div>

    <!-- Divider -->
    @if (preview().studentMappings.length > 0 || preview().unmappedResourceIds.length > 0) {
      <div class="border-t border-gray-100 my-4"></div>
    }

    <!-- Students Section -->
    @if (preview().studentMappings.length > 0 || preview().unmappedResourceIds.length > 0) {
      <div class="space-y-2">
        <div class="flex items-center gap-1.5 text-gray-500 font-medium text-xs">
          <lucide-icon [img]="icons.Users" class="w-4 h-4"></lucide-icon>
          <span>Students ({{ preview().studentMappings.length }})</span>
        </div>
        <div class="space-y-2">
          @for (student of preview().studentMappings; track student.studentId) {
            <div class="flex items-center gap-2 text-xs">
              <lucide-icon [img]="icons.CircleDot" class="w-3 h-3 text-violet-500 flex-shrink-0"></lucide-icon>
              <span class="text-gray-700 font-medium">{{ formatStudentName(student) }}</span>
              @if (student.isNewStudent) {
                <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 font-medium">new</span>
              }
              <!-- File thumbnails -->
              <div class="flex items-center gap-1 ml-auto">
                @for (resource of student.resourcePreviews; track resource.id) {
                  @if (isImage(resource)) {
                    <img
                      [src]="resource.url"
                      [alt]="resource.name"
                      class="w-6 h-6 rounded object-cover border border-gray-200 cursor-pointer hover:ring-2 hover:ring-violet-400 transition-all"
                      (click)="openPreview(resource, $event)"
                    />
                  } @else {
                    <div class="w-6 h-6 rounded bg-gray-100 border border-gray-200 flex items-center justify-center">
                      <lucide-icon [img]="icons.FileText" class="w-3 h-3 text-gray-400"></lucide-icon>
                    </div>
                  }
                }
                @if (getRemainingCount(student.resourceIds.length, student.resourcePreviews.length) > 0) {
                  <div class="w-6 h-6 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-medium">
                    +{{ getRemainingCount(student.resourceIds.length, student.resourcePreviews.length) }}
                  </div>
                }
              </div>
            </div>
          }
          @if (preview().unmappedResourceIds.length > 0) {
            <div class="flex items-center gap-2 text-xs">
              <lucide-icon [img]="icons.CircleDot" class="w-3 h-3 text-gray-400 flex-shrink-0"></lucide-icon>
              <span class="text-gray-400 italic">Unassigned</span>
              <!-- Unassigned file thumbnails -->
              <div class="flex items-center gap-1 ml-auto">
                @for (resource of preview().unmappedResourcePreviews; track resource.id) {
                  @if (isImage(resource)) {
                    <img
                      [src]="resource.url"
                      [alt]="resource.name"
                      class="w-6 h-6 rounded object-cover border border-gray-200 opacity-50 cursor-pointer hover:opacity-100 hover:ring-2 hover:ring-violet-400 transition-all"
                      (click)="openPreview(resource, $event)"
                    />
                  } @else {
                    <div class="w-6 h-6 rounded bg-gray-100 border border-gray-200 flex items-center justify-center opacity-50">
                      <lucide-icon [img]="icons.FileText" class="w-3 h-3 text-gray-400"></lucide-icon>
                    </div>
                  }
                }
                @if (getRemainingCount(preview().unmappedResourceIds.length, preview().unmappedResourcePreviews.length) > 0) {
                  <div class="w-6 h-6 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-medium">
                    +{{ getRemainingCount(preview().unmappedResourceIds.length, preview().unmappedResourcePreviews.length) }}
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>

<!-- Image Preview Modal -->
<app-image-preview-modal
  [previewItem]="previewItem"
  (closed)="closePreview()"
></app-image-preview-modal>
